import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, Card, CardContent, Alert, Grid, Skeleton, Button, Select, MenuItem, TextField, FormControl, InputLabel, Paper, Stack, Chip, Avatar, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Divider } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import MainLayout from '../layouts/MainLayout';
import { getGlobalPerformance, getGlobalRantingPerformance } from '../services/adminService';

// Icons
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DownloadIcon from '@mui/icons-material/Download';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'; 
import PieChartIcon from '@mui/icons-material/PieChart';
import StarIcon from '@mui/icons-material/Star';

const StatWidget = ({ title, value, icon, color, loading }) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden', boxShadow: 3, borderRadius: 2 }}>
    <Box sx={{ position: 'absolute', top: -15, right: -15, opacity: 0.1, transform: 'rotate(-10deg) scale(2.5)', color: color }}>
        {icon}
    </Box>
    <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
        <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 64, height: 64, mr: 2.5 }}>
            {icon}
        </Avatar>
        <Box>
            <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 0.5 }}>{title}</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'text.primary' }}>
                {loading ? <Skeleton width={120} /> : value}
            </Typography>
        </Box>
    </CardContent>
  </Card>
);

const GlobalPerformanceDashboard = () => {
  const [performanceData, setPerformanceData] = useState([]); // MWC Data
  const [rantingData, setRantingData] = useState([]); // Ranting Data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { performance } = await getGlobalPerformance(selectedMonth, selectedYear);
        setPerformanceData(performance || []);

        const rantingPerf = await getGlobalRantingPerformance(selectedMonth, selectedYear);
        setRantingData(rantingPerf || []);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedMonth, selectedYear]);

  // --- MWC PROCESSING ---
  const filteredMWC = useMemo(() => {
    return performanceData.filter(u => !u.unitName.toUpperCase().includes('LAZISNU') && !u.unitName.toUpperCase().includes('PUSAT'));
  }, [performanceData]);

  const mwcTotals = useMemo(() => {
    return filteredMWC.reduce((acc, unit) => ({
      totalPendapatan: acc.totalPendapatan + unit.totalPendapatan,
      totalBeban: acc.totalBeban + unit.totalBeban,
      labaRugi: acc.labaRugi + unit.labaRugi
    }), { totalPendapatan: 0, totalBeban: 0, labaRugi: 0 });
  }, [filteredMWC]);

  const sortedMWC = useMemo(() => [...filteredMWC].sort((a, b) => b.totalPendapatan - a.totalPendapatan), [filteredMWC]);

  const mwcPieData = useMemo(() => {
    if (sortedMWC.length === 0) return [];
    const top5 = sortedMWC.slice(0, 5);
    const others = sortedMWC.slice(5);
    const othersTotal = others.reduce((sum, u) => sum + u.totalPendapatan, 0);
    const data = top5.map((u, index) => ({ id: index, value: u.totalPendapatan, label: u.unitName, color: `hsl(${200 + (index * 20)}, 80%, ${45 + (index * 5)}%)` }));
    if (othersTotal > 0) data.push({ id: 99, value: othersTotal, label: 'Lainnya', color: '#bdbdbd' });
    return data;
  }, [sortedMWC]);

  // --- RANTING PROCESSING ---
  const rantingTotals = useMemo(() => {
    return rantingData.reduce((acc, r) => ({
      totalPendapatan: acc.totalPendapatan + r.totalPendapatan,
      totalBeban: acc.totalBeban + r.totalBeban,
      labaRugi: acc.labaRugi + r.labaRugi
    }), { totalPendapatan: 0, totalBeban: 0, labaRugi: 0 });
  }, [rantingData]);

  const sortedRanting = useMemo(() => [...rantingData].sort((a, b) => b.totalPendapatan - a.totalPendapatan), [rantingData]);

  const rantingPieData = useMemo(() => {
    if (sortedRanting.length === 0) return [];
    const top5 = sortedRanting.slice(0, 5);
    const others = sortedRanting.slice(5);
    const othersTotal = others.reduce((sum, u) => sum + u.totalPendapatan, 0);
    const data = top5.map((u, index) => ({ id: index, value: u.totalPendapatan, label: u.name, color: `hsl(${280 + (index * 20)}, 70%, ${50 + (index * 5)}%)` }));
    if (othersTotal > 0) data.push({ id: 99, value: othersTotal, label: 'Lainnya', color: '#bdbdbd' });
    return data;
  }, [sortedRanting]);


  const overallGlobalTotals = useMemo(() => {
    const totalPemasukan = mwcTotals.totalPendapatan + rantingTotals.totalPendapatan;
    const totalPengeluaran = mwcTotals.totalBeban + rantingTotals.totalBeban;
    const totalLabaRugi = mwcTotals.labaRugi + rantingTotals.labaRugi;
    return { totalPendapatan: totalPemasukan, totalBeban: totalPengeluaran, labaRugi: totalLabaRugi };
  }, [mwcTotals, rantingTotals]);


  // --- PDF GENERATOR ---
  const handleDownloadPdf = () => {
    const doc = new jsPDF();
    const monthName = selectedMonth === 0 ? 'Tahunan' : new Date(0, selectedMonth - 1).toLocaleString('id-ID', { month: 'long' });
    const title = `Laporan Kinerja Global - ${monthName} ${selectedYear}`;
    
    doc.setFontSize(18); doc.setTextColor('#1565c0'); doc.text(title, 14, 20);
    doc.setFontSize(10); doc.setTextColor('#666'); doc.text(`Dicetak pada: ${new Date().toLocaleDateString('id-ID')}`, 14, 28);

    // 1. Overall Global Summary in PDF
    autoTable(doc, {
        head: [['Total Pemasukan Global', 'Total Pengeluaran Global', 'Saldo Global']],
        body: [[`Rp${overallGlobalTotals.totalPendapatan.toLocaleString('id-ID')}`, `Rp${overallGlobalTotals.totalBeban.toLocaleString('id-ID')}`, `Rp${overallGlobalTotals.labaRugi.toLocaleString('id-ID')}`]],
        startY: 35, theme: 'grid', headStyles: { fillColor: [46, 125, 50], halign: 'center' }, bodyStyles: { halign: 'center', fontStyle: 'bold' }
    });

    // 2. MWC Table
    if (sortedMWC.length > 0) {
        doc.text("Peringkat Kinerja Unit MWC", 14, doc.lastAutoTable.finalY + 12);
        autoTable(doc, {
            head: [['Rank', 'Nama Unit', 'Jml Tx', 'Pemasukan', 'Pengeluaran', 'Saldo']],
            body: sortedMWC.map((u, i) => [i + 1, u.unitName, u.totalTransactions, `Rp${u.totalPendapatan.toLocaleString('id-ID')}`, `Rp${u.totalBeban.toLocaleString('id-ID')}`, `Rp${u.labaRugi.toLocaleString('id-ID')}`]),
            startY: doc.lastAutoTable.finalY + 16, theme: 'striped', headStyles: { fillColor: [21, 101, 192] }
        });
    }

    // 3. Ranting Table
    if (sortedRanting.length > 0) {
        doc.addPage();
        doc.text("Peringkat Kinerja Global Ranting", 14, 20);
        autoTable(doc, {
            head: [['Rank', 'Nama Ranting', 'Induk MWC', 'Pemasukan', 'Pengeluaran', 'Saldo']],
            body: sortedRanting.map((u, i) => [i + 1, u.name, u.mwcName, `Rp${u.totalPendapatan.toLocaleString('id-ID')}`, `Rp${u.totalBeban.toLocaleString('id-ID')}`, `Rp${u.labaRugi.toLocaleString('id-ID')}`]),
            startY: 25, theme: 'striped', headStyles: { fillColor: [156, 39, 176] }
        });
    }

    doc.save(`Laporan_Global_Lengkap_${monthName}_${selectedYear}.pdf`);
  };

  if (error) return <MainLayout><Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box></MainLayout>;

  return (
    <MainLayout>
      <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        
        {/* HEADER */}
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, display: 'flex', flexDirection: {xs:'column', md:'row'}, alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0' }}>
            <Box>
                <Typography variant="h5" fontWeight="bold" color="primary.main">Dashboard Kinerja Global</Typography>
                <Typography variant="body2" color="text.secondary">Pemantauan kinerja seluruh MWC dan Ranting.</Typography>
            </Box>
            <Stack direction={{xs:'column', sm:'row'}} spacing={2} sx={{ mt: {xs:2, md:0} }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Periode Bulan</InputLabel>
                    <Select value={selectedMonth} label="Periode Bulan" onChange={(e) => setSelectedMonth(e.target.value)}>
                        <MenuItem value={0}>Semua (Tahunan)</MenuItem>
                        {[...Array(12).keys()].map(m => (<MenuItem key={m + 1} value={m + 1}>{new Date(0, m).toLocaleString('id-ID', { month: 'long' })}</MenuItem>))}
                    </Select>
                </FormControl>
                <TextField size="small" label="Tahun" type="number" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} sx={{ width: 100 }} />
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadPdf} sx={{ borderRadius: 2 }}>Export PDF</Button>
            </Stack>
        </Paper>

        {/* HERO STATS (GLOBAL OVERALL) */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <StatWidget title="Total Pemasukan Global" value={`Rp${overallGlobalTotals.totalPendapatan.toLocaleString('id-ID')}`} icon={<TrendingUpIcon fontSize="large" />} color="#2e7d32" loading={loading} />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatWidget title="Total Pengeluaran Global" value={`Rp${overallGlobalTotals.totalBeban.toLocaleString('id-ID')}`} icon={<TrendingDownIcon fontSize="large" />} color="#d32f2f" loading={loading} />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatWidget title="Saldo Global" value={`Rp${overallGlobalTotals.labaRugi.toLocaleString('id-ID')}`} icon={<AccountBalanceWalletIcon fontSize="large" />} color="#1976d2" loading={loading} />
          </Grid>
        </Grid>

        {/* SECTION 1: MWC PERFORMANCE */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2, display: 'flex', alignItems: 'center' }}>
            <EmojiEventsIcon sx={{ mr: 1 }} /> Laporan Kinerja MWC
        </Typography>
        <Grid container spacing={3} sx={{ mb: 6 }}>
            {/* LEFT: MWC TABLE */}
            <Grid item xs={12} lg={8}>
                <Card sx={{ boxShadow: 3, height: '100%', borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Peringkat MWC (Top Performers)</Typography>
                        <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fff8e1' }}>Rank</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fff8e1' }}>Nama Unit</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#fff8e1' }}>Tx</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#fff8e1' }}>Pemasukan</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#fff8e1' }}>Pengeluaran</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#fff8e1' }}>Saldo</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={6}><Skeleton /></TableCell></TableRow>) : sortedMWC.length === 0 ? <TableRow><TableCell colSpan={6} align="center">Tidak ada data MWC.</TableCell></TableRow> : 
                                        sortedMWC.map((u, idx) => (
                                            <TableRow key={idx} hover>
                                                <TableCell>{idx < 3 ? <Chip label={idx+1} size="small" sx={{ bgcolor: idx===0?'#ffd700':idx===1?'#c0c0c0':'#cd7f32', color: idx===1?'#000':'#fff', fontWeight: 'bold', minWidth: 24 }} /> : `#${idx+1}`}</TableCell>
                                                <TableCell sx={{ fontWeight: 'medium' }}>{u.unitName}</TableCell>
                                                <TableCell align="center">{u.totalTransactions}</TableCell>
                                                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>Rp{u.totalPendapatan.toLocaleString('id-ID')}</TableCell>
                                                <TableCell align="right" sx={{ color: 'text.secondary' }}>Rp{u.totalBeban.toLocaleString('id-ID')}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rp{u.labaRugi.toLocaleString('id-ID')}</TableCell>
                                            </TableRow>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Grid>

            {/* RIGHT: MWC CHART */}
            <Grid item xs={12} lg={4}>
                <Card sx={{ boxShadow: 3, height: '100%', borderRadius: 2 }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <PieChartIcon color="primary" />
                            <Typography variant="h6" fontWeight="bold">Proporsi MWC</Typography>
                        </Stack>
                        <Box sx={{ height: 250, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {loading ? <Skeleton variant="circular" width={200} height={200} /> : (mwcTotals.totalPendapatan === 0 ? <Typography color="text.secondary">Belum ada data.</Typography> : 
                                <PieChart series={[{ data: mwcPieData, innerRadius: 30, outerRadius: 100, paddingAngle: 2, cornerRadius: 4 }]} width={350} height={220} slotProps={{ legend: { direction: 'column', position: { vertical: 'middle', horizontal: 'right' }, itemMarkWidth: 10, itemMarkHeight: 10, labelStyle: { fontSize: 10 } } }} />
                            )}
                        </Box>
                        <Divider sx={{ my: 3 }} />
                        <Box>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>Ringkasan Eksekutif:</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Total <strong>{sortedMWC.length} Unit MWC</strong> aktif. 
                                <strong>{sortedMWC[0]?.unitName || '-'}</strong> memimpin kontribusi.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>

        <Divider sx={{ mb: 6 }} />

        {/* SECTION 2: RANTING GLOBAL PERFORMANCE */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'secondary.main', mb: 2, display: 'flex', alignItems: 'center' }}>
            <StarIcon sx={{ mr: 1 }} /> Laporan Kinerja Global Ranting
        </Typography>
        <Grid container spacing={3}>
            {/* LEFT: RANTING TABLE */}
            <Grid item xs={12} lg={8}>
                <Card sx={{ boxShadow: 3, height: '100%', borderRadius: 2 }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Peringkat Ranting (Top Performers)</Typography>
                        <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5' }}>Rank</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5' }}>Nama Ranting</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5' }}>Induk MWC</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5' }}>Tx</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5' }}>Pemasukan</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5' }}>Pengeluaran</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: '#f3e5f5' }}>Saldo</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={7}><Skeleton /></TableCell></TableRow>) : sortedRanting.length === 0 ? <TableRow><TableCell colSpan={7} align="center">Tidak ada data Ranting.</TableCell></TableRow> : 
                                        sortedRanting.map((u, idx) => (
                                            <TableRow key={idx} hover>
                                                <TableCell>{idx < 3 ? <Chip label={idx+1} size="small" sx={{ bgcolor: idx===0?'#ffd700':idx===1?'#c0c0c0':'#cd7f32', color: idx===1?'#000':'#fff', fontWeight: 'bold', minWidth: 24 }} /> : `#${idx+1}`}</TableCell>
                                                <TableCell sx={{ fontWeight: 'medium' }}>{u.name}</TableCell>
                                                <TableCell>{u.mwcName}</TableCell>
                                                <TableCell align="center">{u.transactionCount}</TableCell>
                                                <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>Rp{u.totalPendapatan.toLocaleString('id-ID')}</TableCell>
                                                <TableCell align="right" sx={{ color: 'text.secondary' }}>Rp{u.totalBeban.toLocaleString('id-ID')}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rp{u.labaRugi.toLocaleString('id-ID')}</TableCell>
                                            </TableRow>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Grid>

            {/* RIGHT: RANTING CHART */}
            <Grid item xs={12} lg={4}>
                <Card sx={{ boxShadow: 3, height: '100%', borderRadius: 2 }}>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <PieChartIcon color="secondary" />
                            <Typography variant="h6" fontWeight="bold">Proporsi Ranting</Typography>
                        </Stack>
                        <Box sx={{ height: 250, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {loading ? <Skeleton variant="circular" width={200} height={200} /> : (rantingTotals.totalPendapatan === 0 ? <Typography color="text.secondary">Belum ada data.</Typography> : 
                                <PieChart series={[{ data: rantingPieData, innerRadius: 30, outerRadius: 100, paddingAngle: 2, cornerRadius: 4 }]} width={350} height={220} slotProps={{ legend: { direction: 'column', position: { vertical: 'middle', horizontal: 'right' }, itemMarkWidth: 10, itemMarkHeight: 10, labelStyle: { fontSize: 10 } } }} />
                            )}
                        </Box>
                        <Divider sx={{ my: 3 }} />
                        <Box>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>Ringkasan Eksekutif:</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Total <strong>{sortedRanting.length} Unit Ranting</strong> aktif. 
                                <strong>{sortedRanting[0]?.name || '-'}</strong> memimpin kontribusi.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>

      </Box>
    </MainLayout>
  );
};

export default GlobalPerformanceDashboard;
